import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from '../../i18n';

export type ToastType = 'info' | 'success' | 'warning' | 'error';

export interface ToastProps {
  id: string;
  message: string;
  type?: ToastType;
  duration?: number;
  onClose: (id: string) => void;
}

const Toast: React.FC<ToastProps> = ({ 
  id, 
  message, 
  type = 'info', 
  duration = 3000, 
  onClose 
}) => {
  const { t } = useTranslation();
  const [isExiting, setIsExiting] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(100);

  const handleClose = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => onClose(id), 300);
  }, [id, onClose]);

  useEffect(() => {
    if (isPaused) return;

    const interval = 50;
    const decrement = (interval / duration) * 100;

    const timer = setInterval(() => {
      setProgress(prev => {
        const next = prev - decrement;
        if (next <= 0) {
          clearInterval(timer);
          handleClose();
          return 0;
        }
        return next;
      });
    }, interval);

    return () => clearInterval(timer);
  }, [duration, isPaused, handleClose]);

  const getIcon = () => {
    switch (type) {
      case 'success':
        return '✓';
      case 'warning':
        return '⚠';
      case 'error':
        return '✕';
      default:
        return 'ℹ';
    }
  };

  const getColors = () => {
    switch (type) {
      case 'success':
        return {
          bg: 'rgba(34, 197, 94, 0.15)',
          border: 'rgba(34, 197, 94, 0.4)',
          icon: '#22c55e',
          progress: '#22c55e'
        };
      case 'warning':
        return {
          bg: 'rgba(251, 191, 36, 0.15)',
          border: 'rgba(251, 191, 36, 0.4)',
          icon: '#fbbf24',
          progress: '#fbbf24'
        };
      case 'error':
        return {
          bg: 'rgba(239, 68, 68, 0.15)',
          border: 'rgba(239, 68, 68, 0.4)',
          icon: '#ef4444',
          progress: '#ef4444'
        };
      default:
        return {
          bg: 'rgba(59, 130, 246, 0.15)',
          border: 'rgba(59, 130, 246, 0.4)',
          icon: '#3b82f6',
          progress: '#3b82f6'
        };
    }
  };

  const colors = getColors();

  return (
    <div
      className={`toast ${isExiting ? 'toast-exit' : 'toast-enter'}`}
      style={{
        background: colors.bg,
        borderColor: colors.border
      }}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="toast-content">
        <span 
          className="toast-icon"
          style={{ color: colors.icon }}
        >
          {getIcon()}
        </span>
        <span className="toast-message">{message}</span>
        <button 
          className="toast-close"
          onClick={handleClose}
          aria-label={t('common.closeNotification')}
        >
          ×
        </button>
      </div>
      <div 
        className="toast-progress"
        style={{
          width: `${progress}%`,
          background: colors.progress
        }}
      />
    </div>
  );
};

export default Toast;

